import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, PhoneCall } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import useEmblaCarousel from 'embla-carousel-react';
import { faqPublicAPI } from '../../api/apiService';

const FAQSection = () => {
    const [faqs, setFaqs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [emblaRef, emblaApi] = useEmblaCarousel({
        align: 'start',
        containScroll: 'trimSnaps',
        dragFree: true
    });

    const [prevBtnEnabled, setPrevBtnEnabled] = useState(false);
    const [nextBtnEnabled, setNextBtnEnabled] = useState(false);

    const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
    const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);

    const onSelect = useCallback(() => {
        if (!emblaApi) return;
        setPrevBtnEnabled(emblaApi.canScrollPrev());
        setNextBtnEnabled(emblaApi.canScrollNext());
    }, [emblaApi]);

    useEffect(() => {
        const fetchFAQs = async () => {
            try {
                const res = await faqPublicAPI.getFAQs();
                setFaqs(res.data);
            } catch (error) {
                console.error("Failed to fetch FAQs:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchFAQs();
    }, []);

    useEffect(() => {
        if (!emblaApi) return;
        onSelect();
        emblaApi.on('select', onSelect);
        emblaApi.on('reInit', onSelect);
    }, [emblaApi, onSelect]);

    return (
        <section className="bg-[#ecfdf5] text-[#052e16] pt-12 pb-16 relative overflow-hidden font-outfit border-t border-emerald-100">
            <div className="max-w-7xl mx-auto px-6 relative z-10">
                {/* Header Section */}
                <div className="flex flex-col lg:flex-row justify-between items-start lg:items-end gap-8 mb-8">
                    <div className="max-w-2xl">
                        <motion.h2 
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            className="text-5xl md:text-7xl font-bold tracking-tight text-[#052e16] mb-6 pl-4 md:pl-8"
                        >
                            Frequently Asked <br />
                            <span className="text-emerald-500 italic" style={{ fontFamily: '"Instrument Serif", serif' }}>Questions</span>
                        </motion.h2>
                    </div>

                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-8 lg:mb-4">
                        <p className="text-[#052e16]/60 text-lg font-medium max-w-xs leading-relaxed">
                            Find answers to common questions about our billing services, project process, and technical expertise.
                        </p>
                        <div className="flex gap-3">
                            <button
                                onClick={scrollPrev}
                                disabled={!prevBtnEnabled}
                                className={`w-14 h-14 rounded-full border-2 flex items-center justify-center transition-all duration-300 ${
                                    prevBtnEnabled 
                                    ? 'border-[#052e16] text-[#052e16] hover:bg-[#052e16] hover:text-white' 
                                    : 'border-gray-100 text-gray-300 cursor-not-allowed'
                                }`}
                            >
                                <ChevronLeft size={24} />
                            </button>
                            <button
                                onClick={scrollNext}
                                disabled={!nextBtnEnabled}
                                className={`w-14 h-14 rounded-full border-2 flex items-center justify-center transition-all duration-300 ${
                                    nextBtnEnabled 
                                    ? 'border-[#052e16] text-[#052e16] hover:bg-[#052e16] hover:text-white' 
                                    : 'border-gray-100 text-gray-300 cursor-not-allowed'
                                }`}
                            >
                                <ChevronRight size={24} />
                            </button>
                        </div>
                    </div>
                </div>

                {/* Carousel Section */}
                {loading ? (
                    <div className="flex justify-center items-center py-20">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
                    </div>
                ) : (
                    <div className="embla overflow-hidden" ref={emblaRef}>
                        <div className="embla__container flex">
                            {faqs.map((faq, index) => {
                                const isDark = index % 2 === 1;
                                return (
                                    <div key={index} className="embla__slide flex-[0_0_85%] sm:flex-[0_0_45%] lg:flex-[0_0_31%] min-w-0 pl-6 first:pl-0">
                                        <motion.div 
                                            initial={{ opacity: 0, x: 20 }}
                                            whileInView={{ opacity: 1, x: 0 }}
                                            viewport={{ once: true }}
                                            transition={{ delay: index * 0.1 }}
                                            className={`h-full min-h-[400px] rounded-[2.5rem] p-10 flex flex-col justify-between transition-transform duration-500 hover:scale-[1.02] ${
                                                isDark 
                                                ? 'bg-[#052e16] text-white' 
                                                : 'bg-[#f8faf9] text-[#052e16] border border-gray-100'
                                            }`}
                                        >
                                            <div>
                                                <h3 className="text-2xl md:text-3xl font-bold leading-tight mb-8">
                                                    {faq.question}
                                                </h3>
                                            </div>
                                            <div>
                                                <p className={`text-base font-medium leading-relaxed ${isDark ? 'text-white/70' : 'text-[#052e16]/60'}`}>
                                                    {faq.answer}
                                                </p>
                                            </div>
                                        </motion.div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

            </div>
        </section>
    );
};

export default FAQSection;
